import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContextNew';
import { CartProvider } from './contexts/CartContext';
import { TimerProvider } from './contexts/TimerContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalCart from './components/GlobalCart';
import GlobalTimer from './components/GlobalTimer';
import GemmaChatbot from './components/GemmaChatbot';
import GlobalToast from './components/GlobalToast';
import { useGoogleMaps } from './hooks/useGoogleMaps';
import { setupGlobalErrorHandler } from './utils/authInterceptor';
import { setupTokenPreservationGuards } from './utils/tokenPreservation';

// Disable console logs in production or when environment variable is set
const shouldDisableConsole = process.env.NODE_ENV === 'production' || process.env.REACT_APP_DISABLE_CONSOLE_LOGS === 'true';

if (shouldDisableConsole) {
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.info = () => {};
  console.debug = () => {};
}

// Import pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPassword from './pages/auth/ForgotPassword';
import GuestPage from './pages/GuestPage';
import LocationPage from './pages/LocationPage';
import HomePage from './pages/HomePage';
import OldHome from './pages/OldHome';
import Home from './pages/Home';
import ProviderProfile from './pages/ProviderProfile';
import ProviderReviews from './pages/ProviderReviews';
import ServiceDetails from './pages/ServiceDetails';
import ProductDetails from './pages/ProductDetails';
import Availability from './pages/Availability';
import Checkout from './pages/Checkout';
import ProductCheckout from './pages/ProductCheckout';
import BookingSuccess from './pages/BookingSuccess';
import LocationCheck from './components/LocationCheck';
import Store from './pages/Store';
import Bookings from './pages/Bookings';
import LeaveReview from './pages/LeaveReview';
import Profile from './pages/Profile';
import ProductOrderListing from './pages/ProductOrderListing';
import ProductOrderDetails from './pages/ProductOrderDetails';
import OrderSuccess from './pages/OrderSuccess';
import CategoryScreen from './pages/CategoryScreen';
import ReferEarn from './pages/ReferEarn';
import Wallet from './pages/Wallet';
import Offers from './pages/Offers';
import FeaturedServices from './pages/FeaturedServices';
import SearchResultsScreen from './pages/SearchResultsScreen';
import LoaderTest from './components/LoaderTest';

import NotificationsPage from './pages/Notifications';
import FavoritesPage from './pages/Favorites';

// Import static pages
import AboutUs from './pages/AboutUs';
import TermsConditions from './pages/TermsConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RescheduleCancellationPolicy from './pages/RescheduleCancellationPolicy';
import ProductCancellationRefundPolicy from './pages/ProductCancellationRefundPolicy';
import FAQ from './pages/FAQ';
import Chat from './pages/Chat';
import ChatConversation from './pages/ChatConversation';
import Addresses from './pages/Addresses';
import PaymentMethods from './pages/PaymentMethods';
import ChangePassword from './pages/ChangePassword';
import EditProfile from './pages/EditProfile';

// Import components
import LoadingSpinner from './components/LoadingSpinner';
import VideoSplashScreen from './components/VideoSplashScreen';
import InstallPrompt from './components/InstallPrompt';

// Create mobile-optimized theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  breakpoints: {
    values: {
      xs: 0,      // Mobile phones
      sm: 600,    // Tablets
      md: 900,    // Small desktops
      lg: 1200,   // Desktops
      xl: 1536,   // Large desktops
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    // Responsive font sizes
    h1: {
      fontSize: '2rem',
      '@media (min-width:600px)': {
        fontSize: '2.5rem',
      },
      '@media (min-width:900px)': {
        fontSize: '3rem',
      },
    },
    h2: {
      fontSize: '1.75rem',
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
      '@media (min-width:900px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontSize: '1.5rem',
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:900px)': {
        fontSize: '2rem',
      },
    },
    h4: {
      fontSize: '1.25rem',
      '@media (min-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontSize: '1.125rem',
      '@media (min-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h6: {
      fontSize: '1rem',
      '@media (min-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    body1: {
      fontSize: '0.875rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '0.75rem',
      '@media (min-width:600px)': {
        fontSize: '0.875rem',
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: '44px', // Touch-friendly minimum height
          padding: '10px 16px',
          textTransform: 'none',
          fontWeight: 500,
          '@media (max-width:599px)': {
            minHeight: '44px',
            fontSize: '0.875rem',
            padding: '10px 14px',
          },
          '@media (min-width:600px)': {
            minHeight: '42px',
            fontSize: '0.9375rem',
            padding: '10px 20px',
          },
          '@media (min-width:900px)': {
            minHeight: '40px',
            fontSize: '1rem',
            padding: '10px 24px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '@media (min-width:600px)': {
            borderRadius: '12px',
          },
          '@media (min-width:900px)': {
            borderRadius: '16px',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width:600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
          '@media (min-width:900px)': {
            paddingLeft: '32px',
            paddingRight: '32px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input': {
            fontSize: '16px', // Prevents zoom on iOS
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: '16px',
          '@media (max-width:599px)': {
            margin: '8px',
            maxWidth: 'calc(100% - 16px)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '12px', // Touch-friendly padding
          '@media (max-width:599px)': {
            padding: '10px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 'auto',
          minHeight: '32px',
          padding: '4px 0',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isGuest } = useAuth();
  
  console.log('🔍 ProtectedRoute: Checking authentication...');
  console.log('🔍 isAuthenticated:', isAuthenticated);
  console.log('🔍 isGuest:', isGuest);
  console.log('🔍 Should render children:', (isAuthenticated || isGuest));
  
  return (isAuthenticated || isGuest) ? children : <Navigate to="/login" />;
};

// Public Route Component (redirects to home if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  console.log('🔍 PublicRoute: Checking authentication...');
  console.log('🔍 isAuthenticated:', isAuthenticated);
  console.log('🔍 Should redirect to home:', isAuthenticated);
  
  return isAuthenticated ? <Navigate to="/home" /> : children;
};

// Splash Screen Wrapper Component
const SplashScreenWrapper = ({ children }) => {
  const [showSplash, setShowSplash] = React.useState(false);
  const [splashComplete, setSplashComplete] = React.useState(false);
  const [checkedSplash, setCheckedSplash] = React.useState(false);

  React.useEffect(() => {
    const shouldShowVideoSplash = () => {
      // Check if running as PWA
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true ||
                    document.referrer.includes('android-app://');
      
      // Check if mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                       window.innerWidth <= 768;
      
      // For localhost testing, check URL parameter (check current URL)
      const urlParams = new URLSearchParams(window.location.search);
      const testPWA = urlParams.get('testPWA') === 'true';
      const forceShow = urlParams.get('splash') === 'true';
      
      // Show splash for mobile devices, PWA, or when forced
      if (!isMobile && !isPWA && !testPWA && !forceShow) {
        console.log('🚫 Desktop browser detected - skip video splash');
        return false;
      }
      
      console.log('✅ Mobile/PWA detected - checking splash conditions');
      
      // Check if first time ever
      const isFirstTimeEver = !localStorage.getItem('appOpenedBefore');
      
      // Check if fresh launch (app was killed or page refresh)
      const isFreshLaunch = !sessionStorage.getItem('appSessionActive');
      
      // Check time-based criteria - only show splash if app closed for more than 15 days
      const lastActiveTime = localStorage.getItem('appLastActiveTime');
      const currentTime = Date.now();
      const timeSinceLastActive = lastActiveTime ? currentTime - parseInt(lastActiveTime) : 0;
      const fifteenDays = 15 * 24 * 60 * 60 * 1000; // 15 days in milliseconds
      const hasBeenAwayLongEnough = timeSinceLastActive > fifteenDays;
      
      if (isFirstTimeEver) {
        console.log('✅ First time ever - show video splash');
        localStorage.setItem('appOpenedBefore', 'true');
        sessionStorage.setItem('appSessionActive', 'true');
        localStorage.setItem('appLastActiveTime', currentTime.toString());
        return true;
      }
      
      // Only show splash on fresh launch if user has been away for more than 15 days
      if (isFreshLaunch && hasBeenAwayLongEnough) {
        console.log('✅ Fresh launch after being away for', Math.round(timeSinceLastActive / (24 * 60 * 60 * 1000)), 'days - show video splash');
        sessionStorage.setItem('appSessionActive', 'true');
        localStorage.setItem('appLastActiveTime', currentTime.toString());
        return true;
      }
      
      // Update last active time even if not showing splash
      localStorage.setItem('appLastActiveTime', currentTime.toString());
      
      // If testPWA=true, always show splash for testing
      if (testPWA || forceShow) {
        console.log('✅ Test mode - forcing splash screen');
        sessionStorage.setItem('appSessionActive', 'true');
        return true;
      }
      
      if (isFreshLaunch && !hasBeenAwayLongEnough) {
        console.log('🚫 Fresh launch but only been away for', Math.round(timeSinceLastActive / (24 * 60 * 60 * 1000)), 'days - skip video splash');
      } else {
        console.log('🚫 Same session - skip video splash');
      }
      return false;
    };
    
    if (!checkedSplash) {
      const shouldShow = shouldShowVideoSplash();
      setShowSplash(shouldShow);
      setSplashComplete(!shouldShow);
      setCheckedSplash(true);
    }
  }, [checkedSplash]);

  // Track user activity to update last active time
  React.useEffect(() => {
    const updateActiveTime = () => {
      localStorage.setItem('appLastActiveTime', Date.now().toString());
    };

    // Update time on various user interactions
    const events = ['click', 'scroll', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, updateActiveTime, { passive: true });
    });

    // Update time on page visibility change (when user switches tabs/apps)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActiveTime();
        sessionStorage.setItem('appSessionActive', 'true');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Update time periodically while app is active (every 30 seconds)
    const interval = setInterval(updateActiveTime, 30000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActiveTime);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  const handleSplashComplete = () => {
    console.log('🎬 Video splash complete');
    setShowSplash(false);
    setSplashComplete(true);
    
    // Trigger install prompt after splash completes
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('splashCompleted'));
    }, 500); // Small delay to ensure app is loaded
  };

  if (showSplash && !splashComplete) {
    return <VideoSplashScreen onComplete={handleSplashComplete} />;
  }

  return children;
};

function App() {
  // Setup global authentication error handler
  useEffect(() => {
    setupGlobalErrorHandler();
    console.log('✅ Global auth interceptor initialized');
  }, []);

  // Setup token preservation guards (CRITICAL: Prevents accidental token clearing)
  useEffect(() => {
    setupTokenPreservationGuards();
    console.log('✅ Customer token preservation guards initialized');
  }, []);

  // Pre-load Google Maps when app starts
  const { isLoaded, isLoading, error } = useGoogleMaps();
  
  useEffect(() => {
    if (isLoaded) {
      console.log('✅ Google Maps pre-loaded successfully');
    } else if (isLoading) {
      console.log('🔄 Google Maps pre-loading...');
    } else if (error) {
      console.error('❌ Google Maps pre-loading failed:', error);
    }
  }, [isLoaded, isLoading, error]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SplashScreenWrapper>
        <Router>
          <AuthProvider>
            <CartProvider>
              <TimerProvider>
                <Routes>
                {/* Public Routes */}
                <Route path="/guest" element={<GuestPage />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                } />
                <Route path="/signup" element={
                  <PublicRoute>
                    <SignupPage />
                  </PublicRoute>
                } />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                
                {/* Protected Routes */}
                <Route path="/location" element={
                  <ProtectedRoute>
                    <LocationPage />
                  </ProtectedRoute>
                } />
                <Route path="/home" element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                } />
                <Route path="/old-home" element={
                  <ProtectedRoute>
                    <OldHome />
                  </ProtectedRoute>
                } />
                {/* Public Provider Profile Routes - Accessible without authentication for marketing */}
                <Route path="/provider/:id" element={<ProviderProfile />} />
                <Route path="/provider/:id/reviews" element={<ProviderReviews />} />
                {/* Public Service and Product Routes - Accessible for marketing and sharing */}
                <Route path="/service/:serviceId/:providerId?" element={<ServiceDetails />} />
                <Route path="/product/:productId/:providerId?" element={<ProductDetails />} />
                <Route path="/location-check" element={
                  <ProtectedRoute>
                    <LocationCheck />
                  </ProtectedRoute>
                } />
                {/* Public Store Route - Accessible without authentication for product browsing */}
                <Route path="/store" element={<Store />} />
                <Route path="/bookings" element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                } />
                <Route path="/bookings/:bookingId" element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                } />
                <Route path="/leave-review" element={
                  <ProtectedRoute>
                    <LeaveReview />
                  </ProtectedRoute>
                } />
                <Route path="/product-orders" element={
                  <ProtectedRoute>
                    <ProductOrderListing />
                  </ProtectedRoute>
                } />
                <Route path="/product-order/:orderId" element={
                  <ProtectedRoute>
                    <ProductOrderDetails />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/referral" element={
                  <ProtectedRoute>
                    <ReferEarn />
                  </ProtectedRoute>
                } />
                <Route path="/wallet" element={
                  <ProtectedRoute>
                    <Wallet />
                  </ProtectedRoute>
                } />
                <Route path="/categories" element={
                  <ProtectedRoute>
                    <CategoryScreen />
                  </ProtectedRoute>
                } />
                <Route path="/offers" element={
                  <ProtectedRoute>
                    <Offers />
                  </ProtectedRoute>
                } />
                <Route path="/featured-services" element={
                  <ProtectedRoute>
                    <FeaturedServices />
                  </ProtectedRoute>
                } />
                <Route path="/search-results" element={
                  <ProtectedRoute>
                    <SearchResultsScreen />
                  </ProtectedRoute>
                } />
                <Route path="/loader-test" element={
                  <ProtectedRoute>
                    <LoaderTest />
                  </ProtectedRoute>
                } />
                <Route path="/salons-near-you" element={
                  <Navigate to="/categories?tab=salon" replace />
                } />
                <Route path="/pros-near-you" element={
                  <Navigate to="/categories?tab=pros" replace />
                } />
                <Route path="/notifications" element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                } />
                <Route path="/favorites" element={
                  <ProtectedRoute>
                    <FavoritesPage />
                  </ProtectedRoute>
                } />
                <Route path="/availability/:providerId?" element={
                  <ProtectedRoute>
                    <Availability />
                  </ProtectedRoute>
                } />
                <Route path="/checkout" element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                } />
                <Route path="/product-checkout" element={
                  <ProtectedRoute>
                    <ProductCheckout />
                  </ProtectedRoute>
                } />
                <Route path="/booking-success" element={
                  <ProtectedRoute>
                    <BookingSuccess />
                  </ProtectedRoute>
                } />
                <Route path="/order-success" element={
                  <ProtectedRoute>
                    <OrderSuccess />
                  </ProtectedRoute>
                } />
                
                {/* Static Pages */}
                <Route path="/about-us" element={
                  <ProtectedRoute>
                    <AboutUs />
                  </ProtectedRoute>
                } />
                <Route path="/terms-conditions" element={
                  <ProtectedRoute>
                    <TermsConditions />
                  </ProtectedRoute>
                } />
                <Route path="/privacy-policy" element={
                  <ProtectedRoute>
                    <PrivacyPolicy />
                  </ProtectedRoute>
                } />
                <Route path="/reschedule-cancellation-policy" element={
                  <ProtectedRoute>
                    <RescheduleCancellationPolicy />
                  </ProtectedRoute>
                } />
                <Route path="/product-cancellation-refund-policy" element={
                  <ProtectedRoute>
                    <ProductCancellationRefundPolicy />
                  </ProtectedRoute>
                } />
                <Route path="/faq" element={
                  <ProtectedRoute>
                    <FAQ />
                  </ProtectedRoute>
                } />
                <Route path="/chat" element={
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                } />
                <Route path="/chat/:chatId" element={
                  <ProtectedRoute>
                    <ChatConversation />
                  </ProtectedRoute>
                } />
                <Route path="/addresses" element={
                  <ProtectedRoute>
                    <Addresses />
                  </ProtectedRoute>
                } />
                <Route path="/payment-methods" element={
                  <ProtectedRoute>
                    <PaymentMethods />
                  </ProtectedRoute>
                } />
                <Route path="/change-password" element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                } />
                <Route path="/edit-profile" element={
                  <ProtectedRoute>
                    <EditProfile />
                  </ProtectedRoute>
                } />
                <Route path="/leave-review" element={
                  <ProtectedRoute>
                    <LeaveReview />
                  </ProtectedRoute>
                } />
                
                {/* Default redirects */}
                <Route path="/" element={<Navigate to="/login" />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
              
              {/* Global Cart - Available on all screens */}
              <GlobalCart />
              
              {/* Global Timer - Available on all screens */}
              <GlobalTimer />
              
              {/* GEMMA Chatbot - Available on all screens */}
              <GemmaChatbot />
              
              
              {/* Global Toast Notifications - Available on all screens */}
              <GlobalToast />
              
              {/* PWA Install Prompt - Shows on mobile devices */}
              <InstallPrompt />
            </TimerProvider>
          </CartProvider>
        </AuthProvider>
      </Router>
      </SplashScreenWrapper>
    </ThemeProvider>
  );
}

export default App;